import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useMerchant } from './MerchantContext';

// Create context
const MerchantDataContext = createContext();

// Custom hook to use the merchant data context
export const useMerchantData = () => {
  const context = useContext(MerchantDataContext);
  if (!context) {
    throw new Error('useMerchantData must be used within a MerchantDataProvider');
  }
  return context;
};

export const MerchantDataProvider = ({ children }) => {
  const { currentMerchant } = useMerchant();

  // Core coordination state
  const [dataVersion, setDataVersion] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Optional cached data for frequently accessed items
  const [cachedMenuItems, setCachedMenuItems] = useState(null);
  const [cachedCategories, setCachedCategories] = useState(null);

  // Track the current merchant ID to detect changes
  const [currentMerchantId, setCurrentMerchantId] = useState(null);

  // Detect merchant changes and notify all components
  useEffect(() => {
    const newMerchantId = currentMerchant?.id;

    // Only trigger if merchant actually changed (not initial load)
    if (currentMerchantId !== null && currentMerchantId !== newMerchantId) {
      // Increment dataVersion to notify all listening components
      setDataVersion(prev => prev + 1);

      // Clear cached data
      setCachedMenuItems(null);
      setCachedCategories(null);

      // Set loading state
      setIsLoading(true);

      // Reset loading state after a short delay
      setTimeout(() => setIsLoading(false), 100);
    }

    // Update tracked merchant ID
    setCurrentMerchantId(newMerchantId);
  }, [currentMerchant?.id, currentMerchantId]);

  // Function to manually notify merchant change (if needed)
  const notifyMerchantChange = useCallback(() => {
    setDataVersion(prev => prev + 1);
    setCachedMenuItems(null);
    setCachedCategories(null);
  }, []);

  const invalidateOrders = useCallback(() => {
    setDataVersion(prev => prev + 1);
  }, []);

  // Function to clear cached data
  const clearCache = useCallback(() => {
    setCachedMenuItems(null);
    setCachedCategories(null);
  }, []);

  // Function to cache menu items
  const cacheMenuItems = useCallback((items) => {
    setCachedMenuItems(items);
  }, []);

  // Function to cache categories
  const cacheCategories = useCallback((categories) => {
    setCachedCategories(categories);
  }, []);

  const value = {
    // Coordination
    dataVersion,
    isLoading,

    // Cached data
    cachedMenuItems,
    cachedCategories,

    // Functions
    notifyMerchantChange,
    invalidateOrders,
    clearCache,
    cacheMenuItems,
    cacheCategories,
  };

  return (
    <MerchantDataContext.Provider value={value}>
      {children}
    </MerchantDataContext.Provider>
  );
};

